import { PlusIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table } from '../../../components/table/Table';
import { getCustomerPortalQuotations } from '../../quotation/models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { decimateAndFormatNumber } from '../../../utils/number';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { useTranslation } from '../../../contexts/translation-context';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';

export const CustomerPortalQuotationsPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const { i18n } = useTranslation();
  const { customer } = useCustomerPortalCustomer();
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getCustomerPortalQuotations(customer.id, variables.cursor, variables.take, {
        search: searchValue,
      });
    },
    [customer.id, searchValue],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  useEffect(() => {
    page.reset();
  }, [searchValue]);

  const tableHeaders: ITableHeader[] = [
    {
      id: 'reference',
      name: i18n('customerPortal.quotes.quoteReference'),
    },
    {
      id: 'date',
      name: i18n('customerPortal.quotes.quoteDate'),
    },
    {
      id: 'expiresAt',
      name: i18n('customerPortal.quotes.expirationDate'),
    },
    {
      id: 'total',
      name: i18n('customerPortal.quotes.total'),
    },
  ];

  const title = i18n('customerPortal.quotes.quotes');
  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={<Breadcrumb currentItem={title} />}
            rightSide={
              <>
                <LinkButton to="requests">{i18n('customerPortal.quotes.requests')}</LinkButton>
                <LinkButton color="primary" iconLeft={<PlusIcon className="button-icon" />} to="requests/new">
                  {i18n('customerPortal.quotes.request')}
                </LinkButton>
              </>
            }
          />
        }
        filters={
          <Input
            type="text"
            placeholder={i18n('customerPortal.quotes.search')}
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={tableHeaders}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(quotation) => {
            const total = quotation.lines.reduce((acc, line) => {
              return acc + line.price;
            }, 0);

            return [
              <Link to={`${quotation.id}`} className="link-text">
                {quotation.reference ?? i18n('draft')}
              </Link>,
              formatDate(quotation.quotationDate),
              formatDate(quotation.expiresAt),
              <div className="whitespace-nowrap">{`€ ${decimateAndFormatNumber(total, 2, {
                decimalSeperator: ',',
              })}`}</div>,
            ];
          }}
        />
      </ListPage>
    </>
  );
};
